/**
 * Envoltura de transacciones (Req. 1.4, 1.7).
 *
 * Toda operación que escriba en más de una tabla debe pasar por aquí. Si algo
 * falla a mitad del camino, IndexedDB revierte y la base queda en el estado
 * previo: por ejemplo, un entrenamiento no queda sin sus series si la app se
 * cierra durante el guardado.
 *
 * Origen: Control de Negocio de Comida; los mensajes ahora llevan tildes.
 */

import { db } from './schema';
import { ErrorDominio } from '@/domain/errors';
import type { Table } from 'dexie';

/** Todas las tablas. Para operaciones amplias como restaurar un respaldo. */
export function todasLasTablas(): Table[] {
  return db.tables;
}

/**
 * Ejecuta una operación de escritura dentro de una transacción atómica.
 *
 * @example
 *   await enEscritura([db.workouts, db.workoutSets], async () => {
 *     await db.workouts.update(id, cambios);
 *     await db.workoutSets.add(serie);
 *   });
 */
export async function enEscritura<T>(
  tablas: readonly Table[],
  operacion: () => Promise<T>,
): Promise<T> {
  try {
    return await db.transaction('rw', tablas as Table[], operacion);
  } catch (error) {
    throw traducirError(error);
  }
}

/** Ejecuta una operación de lectura consistente dentro de una transacción. */
export async function enLectura<T>(
  tablas: readonly Table[],
  operacion: () => Promise<T>,
): Promise<T> {
  try {
    return await db.transaction('r', tablas as Table[], operacion);
  } catch (error) {
    throw traducirError(error);
  }
}

/**
 * Traduce errores de IndexedDB a mensajes en español.
 *
 * Los errores del dominio pasan sin tocarse: ya traen un mensaje pensado para
 * mostrarse en pantalla.
 */
export function traducirError(error: unknown): Error {
  if (error instanceof ErrorDominio) return error;

  if (error instanceof Error) {
    // Dexie envuelve el error original de IndexedDB en `inner`.
    const interno = (error as { inner?: unknown }).inner;
    const nombre =
      interno instanceof Error && error.name === 'AbortError' ? interno.name : error.name;

    if (nombre === 'QuotaExceededError') {
      return new ErrorDominio(
        'cuota_agotada',
        'El almacenamiento del teléfono está lleno y no se pudo guardar. Lo que ya ' +
          'estaba guardado sigue intacto. Haz un respaldo y libera espacio antes de continuar.',
      );
    }

    if (nombre === 'ConstraintError') {
      return new ErrorDominio(
        'restriccion',
        'Ya existe un registro con ese identificador. No se guardó nada.',
      );
    }

    if (nombre === 'VersionError' || nombre === 'UpgradeError') {
      return new ErrorDominio(
        'version_base',
        'Los datos guardados en este teléfono son de una versión más reciente que la ' +
          'app. Actualiza la app antes de continuar.',
      );
    }

    if (nombre === 'DatabaseClosedError') {
      return new ErrorDominio(
        'base_cerrada',
        'Se perdió la conexión con la base de datos. Cierra y vuelve a abrir la app.',
      );
    }

    if (nombre === 'AbortError') {
      return new ErrorDominio(
        'transaccion_abortada',
        'La operación se interrumpió y no se guardó nada. Vuelve a intentarlo.',
      );
    }

    return new ErrorDominio('desconocido', `No se pudo completar la operación: ${error.message}`);
  }

  return new ErrorDominio('desconocido', 'No se pudo completar la operación.');
}

/**
 * Guarda un registro sin riesgo de duplicarlo (Req. 1.9).
 *
 * CONVENCIÓN: cada formulario de alta genera el id con nuevoId() AL ABRIRSE y
 * lo conserva mientras está abierto. Como aquí se guarda con `put`, tocar dos
 * veces "Guardar" o reintentar después de un error actualiza el mismo
 * registro en lugar de crear otro.
 */
export async function guardarRegistro<T, K>(tabla: Table<T, K>, registro: T): Promise<void> {
  await enEscritura([tabla as unknown as Table], async () => {
    await tabla.put(registro);
  });
}

/** Extrae un mensaje presentable de cualquier error. */
export function mensajeDeError(error: unknown): string {
  if (error instanceof ErrorDominio) return error.message;
  if (error instanceof Error) return traducirError(error).message;
  return 'Ocurrió un error inesperado.';
}
